import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/capacitor';
import {
  catchError,
  map,
  mergeMap,
  of,
  startWith,
  switchMap,
  take
} from 'rxjs';
import { Logger } from '@app/utils/logger';
import { AuthActions } from '@components/auth/auth.actions';
import { EventService } from '@providers/event/event.service';
import { DailyJournalService } from './journal';
import { DailyJournalActions } from './journal.actions';
import {
  selectDailyJournalParams,
  selectDailyJournalQuestions
} from './journal.selectors';

/**
 * @description Daily Journal Effects.
 * @exports
 * @class DailyJournalEffects
 */
@Injectable()
export class DailyJournalEffects extends DailyJournalService {
  private readonly actions = inject(Actions);
  private readonly events = inject(EventService);
  private readonly store = inject(Store);
  private readonly logger = Logger.getInstance(DailyJournalEffects.name);

  readonly onAuthLogout_SwitchProfile = createEffect(
    () => {
      return this.actions.pipe(
        ofType(AuthActions.logout, AuthActions.switchProfile),
        map(() => DailyJournalActions.reset())
      );
    },
    { dispatch: true }
  );

  readonly onFetchDailyJournalAnswers = createEffect(() => {
    return this.actions.pipe(
      ofType(DailyJournalActions.fetchDailyJournalAnswer),
      switchMap(({ userDailyJournalId, params }) =>
        this.getDailyJournalAnswers(userDailyJournalId, params).pipe(
          mergeMap((response) => {
            return [
              DailyJournalActions.setDailyJournalAnswer({
                dailyJournalAnswer: response.data ?? []
              }),
              DailyJournalActions.setLoading({
                key: 'dailyJournalAnswer',
                value: false
              })
            ];
          }),
          catchError((error) => {
            this.logger.error('onFetchDailyJournalAnswers Error:', error);
            Sentry.withScope((scope) => {
              scope.setTransactionName('onFetchDailyJournalAnswers');
              Sentry.captureException(error, scope);
            });
            return of(
              DailyJournalActions.setLoading({
                key: 'dailyJournalAnswer',
                value: false
              })
            );
          }),
          startWith(
            DailyJournalActions.setLoading({
              key: 'dailyJournalAnswer',
              value: true
            })
          )
        )
      )
    );
  });

  readonly onFetchDailyJournalFollowUpQuestions = createEffect(() => {
    return this.actions.pipe(
      ofType(DailyJournalActions.fetchDailyJournalFollowUpQuestions),
      switchMap(({ params }) =>
        this.getDailyJournalQuestions({ ...params, isFollowUp: true }).pipe(
          mergeMap((response) => {
            return [
              DailyJournalActions.setDailyJournalFollowUpQuestions({
                dailyJournalFollowUpQuestions: {
                  count: response.data?.count ?? 0,
                  results: response.data?.results ?? []
                }
              }),
              DailyJournalActions.setLoading({
                key: 'dailyJournalFollowUpQuestions',
                value: false
              }),
              DailyJournalActions.setError({
                key: 'dailyJournalFollowUpQuestions',
                value: false
              })
            ];
          }),
          catchError((error) => {
            this.logger.error(
              'onFetchDailyJournalFollowUpQuestions Error:',
              error
            );
            Sentry.withScope((scope) => {
              scope.setTransactionName('onFetchDailyJournalFollowUpQuestions');
              Sentry.captureException(error, scope);
            });
            return of(
              DailyJournalActions.setLoading({
                key: 'dailyJournalFollowUpQuestions',
                value: false
              }),
              DailyJournalActions.setError({
                key: 'dailyJournalFollowUpQuestions',
                value: true
              })
            );
          }),
          startWith(
            DailyJournalActions.setLoading({
              key: 'dailyJournalFollowUpQuestions',
              value: true
            }),
            DailyJournalActions.setError({
              key: 'dailyJournalFollowUpQuestions',
              value: false
            })
          )
        )
      )
    );
  });

  readonly onFetchDailyJournalQuestions = createEffect(() => {
    return this.actions.pipe(
      ofType(DailyJournalActions.fetchDailyJournalQuestions),
      concatLatestFrom(() => [this.store.select(selectDailyJournalQuestions)]),
      switchMap(([{ params }, questions]) =>
        this.getDailyJournalQuestions(params).pipe(
          mergeMap((response) => {
            return [
              DailyJournalActions.setDailyJournalQuestions({
                dailyJournalQuestions: {
                  count: response.data?.count ?? 0,
                  results:
                    (params.offset ?? 0) > 0
                      ? [
                          ...questions.results,
                          ...(response.data?.results ?? [])
                        ]
                      : (response.data?.results ?? [])
                }
              }),
              DailyJournalActions.setLoading({
                key: 'dailyJournalQuestions',
                value: false
              }),
              DailyJournalActions.setError({
                key: 'dailyJournalQuestions',
                value: false
              })
            ];
          }),
          catchError((error) => {
            this.logger.error('onFetchDailyJournalQuestions Error:', error);
            Sentry.withScope((scope) => {
              scope.setTransactionName('onFetchDailyJournalQuestions');
              Sentry.captureException(error, scope);
            });
            return of(
              DailyJournalActions.setLoading({
                key: 'dailyJournalQuestions',
                value: false
              }),
              DailyJournalActions.setError({
                key: 'dailyJournalQuestions',
                value: true
              })
            );
          }),
          startWith(
            DailyJournalActions.setLoading({
              key: 'dailyJournalQuestions',
              value: true
            }),
            DailyJournalActions.setError({
              key: 'dailyJournalQuestions',
              value: false
            })
          )
        )
      )
    );
  });

  readonly onFetchDailyJournalSettings = createEffect(() => {
    return this.actions.pipe(
      ofType(DailyJournalActions.fetchDailyJournalSettings),
      switchMap(({ params }) =>
        this.getDailyJournalSettings(params).pipe(
          mergeMap((response) => {
            const actions = [];
            actions.push(
              DailyJournalActions.setDailyJournalSettings({
                dailyJournalSettings: {
                  count: response.data?.count ?? 0,
                  results: response.data?.results ?? []
                }
              }),
              DailyJournalActions.setLoading({
                key: 'dailyJournalSettings',
                value: false
              })
            );
            if ((response.data?.results ?? []).length === 0) {
              actions.push(
                DailyJournalActions.saveDailyJournalSettingsDefault()
              );
            }
            return actions;
          }),
          catchError((error) => {
            this.logger.error('onFetchDailyJournalSettings Error:', error);
            Sentry.withScope((scope) => {
              scope.setTransactionName('onFetchDailyJournalSettings');
              Sentry.captureException(error, scope);
            });
            return of(
              DailyJournalActions.setLoading({
                key: 'dailyJournalSettings',
                value: false
              })
            );
          }),
          startWith(
            DailyJournalActions.setLoading({
              key: 'dailyJournalSettings',
              value: true
            })
          )
        )
      )
    );
  });

  readonly onFetchDailyJournalSummary = createEffect(() => {
    return this.actions.pipe(
      ofType(DailyJournalActions.fetchDailyJournalSummary),
      switchMap(({ params }) =>
        this.getDailyJournalSummary(params).pipe(
          mergeMap((response) => {
            return [
              DailyJournalActions.setDailyJournalSummary({
                dailyJournalSummary: response.data ?? []
              }),
              DailyJournalActions.setLoading({
                key: 'dailyJournalSummary',
                value: false
              })
            ];
          }),
          catchError((error) => {
            this.logger.error('onFetchDailyJournalSummary Error:', error);
            Sentry.withScope((scope) => {
              scope.setTransactionName('onFetchDailyJournalSummary');
              Sentry.captureException(error, scope);
            });
            return of(
              DailyJournalActions.setLoading({
                key: 'dailyJournalSummary',
                value: false
              })
            );
          }),
          startWith(
            DailyJournalActions.setLoading({
              key: 'dailyJournalSummary',
              value: true
            })
          )
        )
      )
    );
  });

  readonly onFetchDailyJournalSummaryMonths = createEffect(() => {
    return this.actions.pipe(
      ofType(DailyJournalActions.fetchDailyJournalSummaryMonths),
      switchMap(({ params, month, year }) =>
        this.getDailyJournalSummary(params).pipe(
          mergeMap((response) => {
            return [
              DailyJournalActions.setDailyJournalSummaryMonths({
                dailyJournalSummaryMonths: {
                  [`${year}-${month}`]: {
                    timestamp: Date.now(),
                    month,
                    year,
                    data: response.data ?? []
                  }
                }
              }),
              DailyJournalActions.setLoading({
                key: 'dailyJournalSummaryMonths',
                value: false
              })
            ];
          }),
          catchError((error) => {
            this.logger.error('onFetchDailyJournalSummaryMonths Error:', error);
            Sentry.withScope((scope) => {
              scope.setTransactionName('onFetchDailyJournalSummaryMonths');
              Sentry.captureException(error, scope);
            });
            return of(
              DailyJournalActions.setLoading({
                key: 'dailyJournalSummaryMonths',
                value: false
              })
            );
          }),
          startWith(
            DailyJournalActions.setLoading({
              key: 'dailyJournalSummaryMonths',
              value: true
            })
          )
        )
      )
    );
  });

  readonly onFetchDailyJournalTypes = createEffect(() => {
    return this.actions.pipe(
      ofType(DailyJournalActions.fetchDailyJournalTypes),
      switchMap(() =>
        this.getDailyJournalTypes().pipe(
          mergeMap((response) => [
            DailyJournalActions.setDailyJournalTypes({
              dailyJournalTypes: response.data ?? []
            }),
            DailyJournalActions.setLoading({
              key: 'dailyJournalTypes',
              value: false
            })
          ]),
          catchError((error) => {
            this.logger.error('onFetchDailyJournalTypes Error:', error);
            Sentry.withScope((scope) => {
              scope.setTransactionName('onFetchDailyJournalTypes');
              Sentry.captureException(error, scope);
            });
            return of(
              DailyJournalActions.setLoading({
                key: 'dailyJournalTypes',
                value: false
              })
            );
          }),
          startWith(
            DailyJournalActions.setLoading({
              key: 'dailyJournalTypes',
              value: true
            })
          )
        )
      )
    );
  });

  readonly onSaveDailyJournalCheckin = createEffect(() => {
    return this.actions.pipe(
      ofType(DailyJournalActions.saveDailyJournalCheckin),
      concatLatestFrom(() => [
        this.store.select(selectDailyJournalParams).pipe(take(1))
      ]),
      switchMap(([{ payload }, params]) =>
        this.postDailyJournal(payload).pipe(
          mergeMap(() => {
            const actions = [];
            if (
              params.summaryParams?.startDate &&
              params.summaryParams.endDate
            ) {
              actions.push(
                DailyJournalActions.fetchDailyJournalSummary({
                  params: params.summaryParams
                })
              );
            }
            actions.push(
              DailyJournalActions.saveDailyJournalSuccess({
                key: 'saveDailyJournalCheckin',
                value: true
              }),
              DailyJournalActions.setLoading({
                key: 'saveDailyJournalCheckin',
                value: false
              })
            );

            return actions;
          }),
          catchError((error) => {
            this.logger.error('onSaveDailyJournalCheckin Error:', error);
            Sentry.withScope((scope) => {
              scope.setTransactionName('onSaveDailyJournalCheckin');
              Sentry.captureException(error, scope);
            });
            this.events.publish(
              this.events.keys.TOAST_ERROR,
              'Failed to save daily check-in. Please try again.'
            );
            return of(
              DailyJournalActions.setLoading({
                key: 'saveDailyJournalCheckin',
                value: false
              })
            );
          }),
          startWith(
            DailyJournalActions.setLoading({
              key: 'saveDailyJournalCheckin',
              value: true
            })
          )
        )
      )
    );
  });

  readonly onSaveDailyJournalSettings = createEffect(() => {
    return this.actions.pipe(
      ofType(DailyJournalActions.saveDailyJournalSettings),
      switchMap(({ payload }) =>
        this.postDailyJournalSettings(payload).pipe(
          mergeMap(() => [
            DailyJournalActions.fetchDailyJournalSettings({
              params: {
                offset: 0,
                limit: 100,
                includeQuestionDetail: true
              }
            }),
            DailyJournalActions.saveDailyJournalSuccess({
              key: 'saveDailyJournalSetting',
              value: true
            }),
            DailyJournalActions.setLoading({
              key: 'saveDailyJournalSetting',
              value: false
            })
          ]),
          catchError((error) => {
            this.logger.error('onSaveDailyJournalSettings Error:', error);
            Sentry.withScope((scope) => {
              scope.setTransactionName('onSaveDailyJournalSettings');
              Sentry.captureException(error, scope);
            });
            this.events.publish(
              this.events.keys.TOAST_ERROR,
              'Failed to save daily check-in setup. Please try again.'
            );
            return of(
              DailyJournalActions.setLoading({
                key: 'saveDailyJournalSetting',
                value: false
              })
            );
          }),
          startWith(
            DailyJournalActions.setLoading({
              key: 'saveDailyJournalSetting',
              value: true
            })
          )
        )
      )
    );
  });

  readonly onSaveDailyJournalSettingsDefault = createEffect(() => {
    return this.actions.pipe(
      ofType(DailyJournalActions.saveDailyJournalSettingsDefault),
      switchMap(() =>
        this.getDailyJournalQuestions({
          offset: 0,
          limit: 20,
          isFollowUp: false
        }).pipe(
          take(1),
          map(
            (response) =>
              response.data?.results
                .filter((q) => q.isDefault)
                .map((q) => q.id) ?? []
          ),
          map((dailyJournalQuestionIds: Array<string>) => {
            const payload = {
              dailyJournalQuestionIds
            };
            this.logger.log(
              'onSaveDailyJournalSettingsDefault dailyJournalQuestionIds:',
              dailyJournalQuestionIds
            );
            return DailyJournalActions.saveDailyJournalSettings({
              payload
            });
          }),
          catchError((error) => {
            this.logger.error(
              'onSaveDailyJournalSettingsDefault Error:',
              error
            );
            Sentry.withScope((scope) => {
              scope.setTransactionName('onSaveDailyJournalSettingsDefault');
              Sentry.captureException(error, scope);
            });
            return of();
          })
        )
      )
    );
  });
}
